/**
 * URL-encoded state for the Tasks page.
 *
 * Filters, the active tab, and the page live in the query string so a
 * filtered view is shareable and survives reload. The `tab` param drives
 * the table preset (Live = recent / History = unfiltered / Failures =
 * status=failed) and the remaining params layer freely on top.
 *
 * The read and the write are pure so the task detail page, which carries
 * this query string in its own URL, parses it with the same code.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ListTasksFilters } from '@optum/api-client/endpoints/tasks';
import type { TaskOutcome, TaskSource, TaskStatus } from '@optum/api-client/types';

import { listsTopLevel } from './linked-rows.js';

/** `settings` is the platform route's runtime configuration, not a list view. */
export type TasksTab = 'all' | 'live' | 'history' | 'failures' | 'settings';
export type PageSize = 25 | 50 | 100;

const TAB_VALUES: readonly TasksTab[] = ['all', 'live', 'history', 'failures', 'settings'];

const STATUS_VALUES: readonly TaskStatus[] = [
  'pending',
  'running',
  'streaming',
  'completed',
  'failed',
  'cancelled',
];

const SOURCE_VALUES: readonly TaskSource[] = [
  'client',
  'operator_test',
  'linkage',
];
const OUTCOME_VALUES: readonly TaskOutcome[] = ['true', 'false', 'none'];

export const PAGE_SIZES: readonly PageSize[] = [25, 50, 100];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

export interface TasksUrlState {
  tab: TasksTab;
  /** Task-id substring. Client-side: the list endpoint has no id filter. */
  search: string;
  organizationId: string;
  agentId: string;
  capabilityId: string;
  status: TaskStatus[];
  source: TaskSource[];
  /** The result's boolean `success`; empty means any. */
  success: TaskOutcome | '';
  createdAfter: string;
  createdBefore: string;
  /** 1-based. */
  page: number;
  size: PageSize;
}

export interface TasksUrlStateApi extends TasksUrlState {
  setTab: (tab: TasksTab) => void;
  patch: (next: Partial<TasksUrlState>) => void;
  clearFilters: () => void;
}

export function readTasksUrlState(sp: URLSearchParams): TasksUrlState {
  return {
    tab: readEnum(sp.get('tab'), TAB_VALUES, 'all'),
    search: sp.get('q') ?? '',
    organizationId: sp.get('org') ?? '',
    agentId: sp.get('agent') ?? '',
    capabilityId: sp.get('cap') ?? '',
    status: readMulti(sp.getAll('status'), STATUS_VALUES),
    source: readMulti(sp.getAll('source'), SOURCE_VALUES),
    success: readEnum(sp.get('success'), OUTCOME_VALUES, ''),
    createdAfter: sp.get('after') ?? '',
    createdBefore: sp.get('before') ?? '',
    page: readPage(sp.get('page')),
    size: readSize(sp.get('size')),
  };
}

/** The query string for a state, with every default left out. */
export function writeTasksUrlState(state: TasksUrlState): URLSearchParams {
  const next = new URLSearchParams();
  if (state.tab !== 'all') next.set('tab', state.tab);
  applyScalar(next, 'q', state.search);
  applyScalar(next, 'org', state.organizationId);
  applyScalar(next, 'agent', state.agentId);
  applyScalar(next, 'cap', state.capabilityId);
  applyMulti(next, 'status', state.status);
  applyMulti(next, 'source', state.source);
  applyScalar(next, 'success', state.success);
  applyScalar(next, 'after', state.createdAfter);
  applyScalar(next, 'before', state.createdBefore);
  if (state.page > 1) next.set('page', String(state.page));
  if (state.size !== DEFAULT_PAGE_SIZE) next.set('size', String(state.size));
  return next;
}

/**
 * A change of anything but the page starts over at page 1: the old page
 * number indexed a different result set.
 */
export function patchTasksUrlState(
  state: TasksUrlState,
  delta: Partial<TasksUrlState>,
): TasksUrlState {
  const keys = Object.keys(delta) as (keyof TasksUrlState)[];
  const pageOnly = keys.length === 1 && keys[0] === 'page';
  return { ...state, ...(pageOnly ? {} : { page: 1 }), ...delta };
}

export function useTasksUrlState(): TasksUrlStateApi {
  const [sp, setSp] = useSearchParams();

  const state = useMemo<TasksUrlState>(() => readTasksUrlState(sp), [sp]);

  const patch = useCallback(
    (delta: Partial<TasksUrlState>) => {
      setSp(writeTasksUrlState(patchTasksUrlState(state, delta)), { replace: true });
    },
    [setSp, state],
  );

  const setTab = useCallback((tab: TasksTab) => patch({ tab }), [patch]);

  // The size is a preference, not a filter, so it survives a clear.
  const clearFilters = useCallback(() => {
    setSp(
      writeTasksUrlState({
        ...readTasksUrlState(new URLSearchParams()),
        tab: state.tab,
        size: state.size,
      }),
      { replace: true },
    );
  }, [setSp, state.size, state.tab]);

  return { ...state, setTab, patch, clearFilters };
}

/**
 * The statuses each tab means. A tab is a saved status filter, so it has to
 * override the picked one — otherwise "Live" would show completed rows left
 * over from a filter the operator set on another tab.
 */
const TAB_STATUS: Record<TasksTab, readonly TaskStatus[]> = {
  all: [],
  live: ['pending', 'running', 'streaming'],
  history: ['completed', 'cancelled'],
  failures: ['failed'],
  settings: [],
};

/**
 * Project the URL state to backend filter args. A non-`all` tab forces its own
 * statuses, explicitly, so tab semantics are never overridden by stale filter
 * state carried in from another tab.
 */
export function buildListFilters(state: TasksUrlState): ListTasksFilters {
  const forced = TAB_STATUS[state.tab];
  const status = forced.length > 0 ? [...forced] : state.status;
  return {
    ...(state.organizationId ? { organization_id: state.organizationId } : {}),
    ...(state.agentId ? { agent_id: state.agentId } : {}),
    ...(state.capabilityId ? { capability_id: state.capabilityId } : {}),
    ...(status.length > 0 ? { status } : {}),
    ...(state.source.length > 0 ? { source: state.source } : {}),
    // Linked tasks sit under their first task unless asked for by source (Feature 71, D33).
    ...(listsTopLevel(state.source) ? { top_level: true } : {}),
    ...(state.success ? { success: state.success } : {}),
    ...(state.createdAfter ? { created_after: state.createdAfter } : {}),
    ...(state.createdBefore ? { created_before: state.createdBefore } : {}),
    limit: state.size,
    offset: (state.page - 1) * state.size,
  };
}

/**
 * The query string a task screen carries, with `page` always present. The
 * list leaves page 1 out of its own URL; the task screen reads the key's
 * presence as "opened from a list", so the row link and the prev/next
 * step both write it.
 */
export function withPage(search: string, page: number): string {
  const params = new URLSearchParams(search);
  params.set('page', String(page));
  return `?${params.toString()}`;
}

function readEnum<T extends string>(
  raw: string | null,
  allowed: readonly T[],
  fallback: T,
): T {
  return (allowed as readonly string[]).includes(raw ?? '') ? (raw as T) : fallback;
}

function readMulti<T extends string>(raw: string[], allowed: readonly T[]): T[] {
  const set = new Set(allowed as readonly string[]);
  return raw.filter((v) => set.has(v)) as T[];
}

function readPage(raw: string | null): number {
  const n = Number.parseInt(raw ?? '', 10);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

function readSize(raw: string | null): PageSize {
  const n = Number.parseInt(raw ?? '', 10);
  return (PAGE_SIZES as readonly number[]).includes(n) ? (n as PageSize) : DEFAULT_PAGE_SIZE;
}

function applyScalar(params: URLSearchParams, key: string, value: string): void {
  if (value) params.set(key, value);
}

function applyMulti(params: URLSearchParams, key: string, values: readonly string[]): void {
  for (const v of values) params.append(key, v);
}
